import React, { useMemo, useState } from "react";

const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Europe/Berlin calendar date — matches admin calendar API day keys. */
const toBerlinISODate = (d) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(new Date(d));

const getBerlinWeekday = (d) => {
  const short = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    weekday: "short",
  }).format(new Date(d));
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[short] ?? new Date(d).getDay();
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const startOfWeekMondayBerlin = (date) => {
  const key = toBerlinISODate(date);
  const [y, m, day] = key.split("-").map(Number);
  const weekday = getBerlinWeekday(date);
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  return new Date(y, m - 1, day + mondayOffset, 12, 0, 0);
};

const weekDaysFromBerlin = (anchorDate) => {
  const monday = startOfWeekMondayBerlin(anchorDate);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
};

const bookingCountForDay = (markedDays, iso) => {
  if (!markedDays || !iso) return 0;
  const val = markedDays[iso];
  if (val == null) return 0;
  if (Array.isArray(val)) return val.length;
  if (typeof val === "number") return val;
  return 0;
};

const isClosedDay = (date, { weekdaysOnly, closedWeekday, onlyWeekday }) => {
  const day = date.getDay(); // 0=Sun .. 6=Sat
  if (onlyWeekday >= 0 && day !== onlyWeekday) return true;
  if (weekdaysOnly) return day === 0 || day === 6;
  return closedWeekday >= 0 && day === closedWeekday;
};

const Calendar = ({
  selectedDate,
  onSelect,
  minDate,
  maxDate,
  /** Dr. Hoffmann: Mon–Fri only (Sat/Sun closed). */
  weekdaysOnly = false,
  closedWeekday = -1,
  /** When set (0–6), only this weekday is bookable (e.g. 4 = Thursday). */
  onlyWeekday = -1,
  /** Optional custom predicate to disable specific dates (greyed out). */
  disableDate,
  disableDateTitle,
  closedDayTitle,
  markedDays,
  onMonthChange,
  dateLocale,
  labels = {},
}) => {
  const prevLabel = labels.prev ?? "← Zurück";
  const nextLabel = labels.next ?? "Weiter →";
  const closedLabel =
    closedDayTitle ||
    labels.closedDay ||
    (weekdaysOnly
      ? "Samstag und Sonntag geschlossen"
      : "Dienstag ist Ruhetag");
  const appointmentTitle =
    labels.appointments ?? ((count) => `${count} Termin(e)`);
  const today = startOfDay(new Date());
  const initial = selectedDate || today;
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  const changeMonth = (year, month) => {
    setViewYear(year);
    setViewMonth(month);
    onMonthChange?.(year, month);
  };

  const min = startOfDay(minDate || today);
  const max = startOfDay(maxDate || new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000));

  const cells = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const startOffset = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const grid = [];
    for (let i = 0; i < startOffset; i += 1) {
      const d = new Date(viewYear, viewMonth, 1 - (startOffset - i));
      grid.push({ date: d, otherMonth: true });
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      grid.push({ date: new Date(viewYear, viewMonth, d), otherMonth: false });
    }
    while (grid.length % 7 !== 0) {
      const last = grid[grid.length - 1].date;
      grid.push({
        date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
        otherMonth: true,
      });
    }
    return grid;
  }, [viewYear, viewMonth]);

  const canPrev = () => {
    const monthStart = new Date(viewYear, viewMonth, 1);
    return monthStart > new Date(min.getFullYear(), min.getMonth(), 1);
  };
  const canNext = () => {
    const monthEnd = new Date(viewYear, viewMonth + 1, 0);
    return monthEnd < max;
  };

  const monthTitle = dateLocale
    ? new Date(viewYear, viewMonth, 1).toLocaleDateString(dateLocale, {
        month: "long",
        year: "numeric",
      })
    : `${MONTHS_DE[viewMonth]} ${viewYear}`;

  const weekdayLabels = dateLocale
    ? Array.from({ length: 7 }, (_, i) =>
        new Date(2024, 0, 1 + i).toLocaleDateString(dateLocale, { weekday: "short" })
      )
    : WEEKDAYS_DE;

  const closedOpts = { weekdaysOnly, closedWeekday, onlyWeekday };

  return (
    <div>
      <div className="calendar-nav">
        <button
          type="button"
          onClick={() => {
            const m = viewMonth - 1;
            if (m < 0) changeMonth(viewYear - 1, 11);
            else changeMonth(viewYear, m);
          }}
          disabled={!canPrev()}
        >
          {prevLabel}
        </button>
        <span className="calendar-title">{monthTitle}</span>
        <button
          type="button"
          onClick={() => {
            const m = viewMonth + 1;
            if (m > 11) changeMonth(viewYear + 1, 0);
            else changeMonth(viewYear, m);
          }}
          disabled={!canNext()}
        >
          {nextLabel}
        </button>
      </div>

      <div className="calendar-grid">
        {weekdayLabels.map((d) => (
          <div className="calendar-head" key={d}>
            {d}
          </div>
        ))}
        {cells.map(({ date, otherMonth }, idx) => {
          const isOutOfRange = date < min || date > max;
          const isClosed = isClosedDay(date, closedOpts);
          const isCustomDisabled = Boolean(disableDate?.(date));
          const disabled = otherMonth || isOutOfRange || isClosed || isCustomDisabled;
          const selected = isSameDay(selectedDate, date);
          const iso = toISODate(date);
          const bookingCount = bookingCountForDay(markedDays, iso);
          const hasBookings =
            bookingCount > 0 && !otherMonth && !isClosed && !isCustomDisabled;
          return (
            <button
              key={idx}
              type="button"
              className={[
                "calendar-cell",
                otherMonth ? "is-other-month" : "",
                (isClosed || isCustomDisabled) && !otherMonth ? "is-closed" : "",
                hasBookings ? "has-bookings" : "",
                selected ? "is-selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => !disabled && onSelect(date)}
              disabled={disabled}
              title={
                isCustomDisabled
                  ? disableDateTitle
                  : isClosed
                  ? closedLabel
                  : hasBookings
                    ? appointmentTitle(bookingCount)
                    : undefined
              }
            >
              <span className="calendar-cell-day">{date.getDate()}</span>
              {hasBookings && (
                <span className="calendar-cell-mark" aria-hidden="true">
                  {bookingCount > 1 ? bookingCount : "·"}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export {
  toISODate,
  toBerlinISODate,
  startOfWeekMondayBerlin,
  weekDaysFromBerlin,
  addDays as addCalendarDays,
};
export default Calendar;
